import { Button } from '@heidi/design-system/button';
import { HeidiButton } from '@/components/chat/components/HeidiButton';
import UserNotesBlock from '@/components/chat/components/UserNotesBlock';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import {
  TypographyCaptionBold,
  TypographyP2,
} from '@heidi/design-system/typography';
import { SignUpPromptModalChat } from '@/app-shell/auth/sign-up-prompt-modal-chat';
import { useHasPermissionToReplace } from '@/components/ui/ask-ai-input/hooks/useHasPermissionToReplace';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { resolveTimelineErrorDisplay } from '@heidi/evidence/data';
import { useIntl } from 'react-intl';
import { useTranslations } from '@heidi/runtime/i18n';
import { ChevronDownIcon, FilePlus, ListPlus, Replace } from 'lucide-react';
import { useState, type JSX } from 'react';
import { useChatResponseActions } from '../hooks/useChatResponseActions';
import { CustomerFeedback } from './customer-feedback';
import { track } from '@/lib/analytics/track';
import type { EvidenceResponsePorts } from '@heidi/evidence';
export type { Citation } from '@heidi/evidence/contracts';
export { getDomainFromUrl, normalizeDomain } from '@heidi/evidence/contracts';
export { getFaviconUrl } from '@/utils/faviconCache';

export interface ActionsPopoverProps {
  onAddToNote?: () => void;
  onReplaceNote?: () => void;
  onCreateNewTab?: () => void;
  isSessionless?: boolean;
}

export const ActionsPopover = ({
  onAddToNote,
  onReplaceNote,
  onCreateNewTab,
  isSessionless = false,
}: ActionsPopoverProps): JSX.Element | null => {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const { hasPermissionToReplace } = useHasPermissionToReplace();
  const { isAuthenticated } = useIsAuthenticated();

  // Hide the entire popover for unauthenticated users on mobile
  if (!isAuthenticated) return null;

  // For logged-out sessionless users, always show the actions dropdown but intercept clicks
  const shouldShowActionsForPreview = isSessionless && !isAuthenticated;

  // Hide popover if no actions are available and not a preview scenario
  const hasAnyActions =
    hasPermissionToReplace  || shouldShowActionsForPreview;
  if (!hasAnyActions) return null;

  const handleAction = (action?: () => void) => {
    // If user is not authenticated in sessionless mode, show signup modal
    if (shouldShowActionsForPreview) {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceSignUpWallViewed,
        props: {
          wall_type: EvidenceSignUpWall.ActionsWall,
        },
      });
      setShowSignUpModal(true);
      setOpen(false);
      return;
    }

    action?.();
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="md" aria-label={t('chat.moreActions')}>
            <TypographyCaptionBold className="text-text-secondary">
              {t('chat.actions')}
            </TypographyCaptionBold>
            <ChevronDownIcon className="text-text-secondary" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end" side="top" sideOffset={4}>
          <div className="space-y-1">
            {/* For logged-out users, show preview of actions */}
            {shouldShowActionsForPreview ? (
              <>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <ListPlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.addToNote')}</TypographyP2>
                </Button>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <Replace className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.replaceNote')}</TypographyP2>
                </Button>
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction()}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <FilePlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">{t('chat.createNewTab')}</TypographyP2>
                </Button>
              </>
            ) : (
              <>
                {/* Add to note/document */}
                {hasPermissionToReplace && (
                  <Button size="lg"
                    disableAutomaticSvgSizing
                    onClick={() => handleAction(onAddToNote)}
                    variant="ghost"
                    className="w-full justify-start gap-2 text-sm"
                  >
                    <ListPlus className="size-4 text-text-tertiary" />
                    <TypographyP2 className="text-text-primary">{t('chat.addToNote')}</TypographyP2>
                  </Button>
                )}

                {/* Replace note/document */}
                {hasPermissionToReplace && (
                  <Button size="lg"
                    disableAutomaticSvgSizing
                    onClick={() => handleAction(onReplaceNote)}
                    variant="ghost"
                    className="w-full justify-start gap-2 text-sm"
                  >
                    <Replace className="size-4 text-text-tertiary" />
                    <TypographyP2 className="text-text-primary">
                      {t('chat.replaceNote')}
                    </TypographyP2>
                  </Button>
                )}

                {/* Create new tab */}
                <Button size="lg"
                  disableAutomaticSvgSizing
                  onClick={() => handleAction(onCreateNewTab)}
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                >
                  <FilePlus className="size-4 text-text-tertiary" />
                  <TypographyP2 className="text-text-primary">
                    {t('chat.createNewTab')}
                  </TypographyP2>
                </Button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Signup modal for logged-out users */}
      {shouldShowActionsForPreview && (
        <SignUpPromptModalChat
          isOpen={showSignUpModal}
          onClose={() => setShowSignUpModal(false)}
          title={t('chat.signUpToAccessActions')}
          subtitle={t('chat.signUpToAccessActionsSubtitle')}
        />
      )}
    </>
  );
};

export const ComplianceErrorMessage = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const displayMessage = resolveTimelineErrorDisplay(message, (descriptor) =>
    intl.formatMessage(descriptor)
  );
  const tryAgainLabel = intl.formatMessage({
    id: 'h76/LU2lTl',
    defaultMessage: 'Try again',
    description: 'REG-51 / SWR-35 recovery button on Evidence query errors',
  });

  return (
    <div
      className="w-full space-y-2 rounded bg-transparent"
      data-testid="query-error-message"
    >
      <div className="flex items-center gap-2">
        <div className="w-full">
          <p className="mb-2 break-words text-sm">{displayMessage}</p>
        </div>
      </div>

      {onRetry && (
        <div className="flex flex-row justify-start">
          <Button
            variant="outline"
            size="md"
            onClick={() => void onRetry()}
            className="shrink-0 transition-colors"
            aria-label={tryAgainLabel}
            data-testid="query-error-try-again"
          >
            {tryAgainLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export const chatResponsePorts: EvidenceResponsePorts = {
  useResponseActions: useChatResponseActions,
  ActionsMenu: ActionsPopover,
  UserNotes: UserNotesBlock,
  HeidiButton,
  CustomerFeedback,
};
